// Session model — see PRD §3.4. A session is a continuous block of real work, not the
// lifetime of a tab:
//   - shared across tabs (session_id in localStorage)
//   - rotated after `sessionTimeoutMs` of inactivity, or when the user changes
//   - each event carries a monotonic `seq` for ordering + gap detection
//   - `tab_id` (sessionStorage) disambiguates parallel-tab activity within a session

import { config } from "./config"

// dipi-app-front has no `uuid` dependency (PUIG's SDK uses it); crypto.randomUUID is available in
// every browser we support. The fallback only exists so a non-secure context can't break init.
function newId() {
    try {
        if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID()
    } catch { /* fall through */ }
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
        const r = (Math.random() * 16) | 0
        return (c === "x" ? r : (r & 0x3) | 0x8).toString(16)
    })
}

const SESSION_KEY = "analytics.session"   // localStorage: { id, startTs, lastActivityTs, seq, userId }
const TAB_KEY = "analytics.tab"           // sessionStorage: tab uuid

// Per-tab id: born with the tab, dies with it.
let tabId = null
function getTabId() {
    if (tabId) return tabId
    try {
        tabId = sessionStorage.getItem(TAB_KEY)
        if (!tabId) {
            tabId = newId()
            sessionStorage.setItem(TAB_KEY, tabId)
        }
    } catch {
        tabId = tabId || newId() // sessionStorage blocked — fall back to in-memory
    }
    return tabId
}

function readStored() {
    try {
        const raw = localStorage.getItem(SESSION_KEY)
        return raw ? JSON.parse(raw) : null
    } catch {
        return null
    }
}

function writeStored(s) {
    try {
        localStorage.setItem(SESSION_KEY, JSON.stringify(s))
    } catch { /* localStorage blocked — session stays in-memory for this tab only */ }
}

let current = null

function startSession(userId, now) {
    current = { id: newId(), startTs: now, lastActivityTs: now, seq: 0, userId: userId ?? null }
    writeStored(current)
    return current
}

// Returns the live session, rotating it if it expired (inactivity) or the user changed.
// `onRotate(reason, prev, next)` lets the caller emit session_started/session_ended.
export function ensureSession(userId, now, onRotate) {
    if (!current) current = readStored()

    const expired = current && (now - current.lastActivityTs) > config.sessionTimeoutMs
    // Rotate when the identity changes away from a known user — including logout
    // (user → null), so anonymous post-logout activity doesn't share the logged-in session.
    const userChanged = current && current.userId != null && userId !== current.userId

    if (!current || expired) {
        const prev = current
        const next = startSession(userId, now)
        onRotate?.(expired ? "timeout" : "new", prev, next)
        return next
    }
    if (userChanged) {
        const prev = current
        const next = startSession(userId, now)
        onRotate?.("user_change", prev, next)
        return next
    }
    // Adopt the user id once it becomes known on an anonymous session.
    if (current.userId == null && userId != null) current.userId = userId
    return current
}

// Bump activity + advance the per-session sequence. Call once per emitted event.
export function touchSession(now) {
    if (!current) return { sessionId: null, seq: 0, tabId: getTabId() }
    // Reduce cross-tab seq clobbering: adopt the highest seq another tab has persisted for
    // this same session before advancing. Not fully atomic (localStorage RMW isn't), but
    // keeps seq roughly monotonic per session; consumers should order by ts as the tiebreak.
    const stored = readStored()
    if (stored && stored.id === current.id && stored.seq > current.seq) current.seq = stored.seq
    current.lastActivityTs = now
    current.seq += 1
    writeStored(current)
    return { sessionId: current.id, seq: current.seq, tabId: getTabId() }
}

export function getCurrent() {
    return current
}
